/**
 * API Service Helper
 * Handles authenticated API requests with proper headers
 */
import { API_URL } from '../config/api';
import adminAuthService from './adminAuthService';

/**
 * Make an authenticated API request
 * Automatically includes the Authorization header with the current session token
 */
export const authFetch = async (endpoint, options = {}) => {
  const session = adminAuthService.getCurrentSession();
  
  if (!session?.access_token) {
    throw new Error('No authentication session found. Please login again.');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
    ...options.headers
  };

  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers
    });

    // If unauthorized, clear session and redirect to login
    if (response.status === 401) {
      adminAuthService.logout();
      window.location.href = '/admin/login';
      throw new Error('Session expired. Please login again.');
    }

    return response;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

/**
 * Make an authenticated GET request
 */
export const authGet = async (endpoint) => {
  const response = await authFetch(endpoint, {
    method: 'GET'
  });
  return response.json();
};

/**
 * Make an authenticated POST request
 */
export const authPost = async (endpoint, data) => {
  const response = await authFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(data)
  });
  return response.json();
};

/**
 * Make an authenticated PUT request
 */
export const authPut = async (endpoint, data) => {
  const response = await authFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
  return response.json();
};

/**
 * Make an authenticated PATCH request
 */
export const authPatch = async (endpoint, data) => {
  const response = await authFetch(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data)
  });
  return response.json();
};

/**
 * Make an authenticated DELETE request
 */
export const authDelete = async (endpoint) => {
  const response = await authFetch(endpoint, {
    method: 'DELETE'
  });
  return response.json();
};

export default {
  fetch: authFetch,
  get: authGet,
  post: authPost,
  put: authPut,
  patch: authPatch,
  delete: authDelete
};
